import {
  every,
  isArray,
  isEqual,
  isNumber,
  isObject,
  isString,
  keys,
  LoDashStatic,
} from 'lodash';
import {
  IComponent,
  IEventData,
  IFocusDrillData,
  IModel,
  ISugarApp,
} from './mfe.interfaces';
import {
  DynamicFields,
  setDynamicValues,
  getDynamicValues,
  gainsightConstants,
} from '../../app/frontend/js/common';
import { useEffect, useRef } from 'react';
import {
  IDashletFilter,
  IDashboardFilter,
} from '../../app/frontend/js/common/utilities/sugar-filter-converter/sugar-filter-converter.interfaces';

declare const _: LoDashStatic;
declare const App: ISugarApp;

// a simplified recursive object comparator
export const isFiltersDefEqual = (obj1, obj2, lexigraphicalLevel = 0) => {
  if (isObject(obj1 ?? {})) {
    const sameKeys = isEqual(keys(obj1 ?? {}).sort(), keys(obj2 ?? {}).sort());

    return (
      lexigraphicalLevel < 10 &&
      sameKeys &&
      every(keys(obj1 ?? {}), _key =>
        isFiltersDefEqual(obj1[_key], obj2[_key], lexigraphicalLevel + 1),
      )
    );
  }

  if (isArray(obj1) && isArray(obj2) && !isEqual(obj1, obj2)) {
    return false;
  }

  return (isString(obj1) || isNumber(obj1)) && isEqual(obj1, obj2);
};

export function findComponent(
  rootElem: IComponent,
  matcher: (component: IComponent) => boolean,
  checked: IComponent[] = [],
) {
  if (_.includes(checked, rootElem)) {
    return;
  }
  checked.push(rootElem);
  const isMatch = matcher(rootElem);
  if (isMatch) {
    return rootElem;
  }
  const components = rootElem._components || [];
  let match;
  for (let i = 0; i < components.length && !match; i++) {
    const c = components[i];
    match = findComponent(c, matcher, checked);
  }
  return match;
}

export const getMfeLayoutComponent = (module: string) =>
  _(['record-dashlet', 'list-dashlet'])
    .chain()
    .map(layout => App.metadata.getLayout(module, layout))
    .map('components')
    .flatten() // flatMap is not supported on different versions of lodash
    .map('view')
    .filter(Boolean)
    .find(view =>
      // LBL_SUGAR_DISCOVER_DASHLET is now translated in core >= 14.2
      _.includes(['Sugar Discover', 'LBL_SUGAR_DISCOVER_DASHLET'], view?.name),
    )
    .value();

export function findNamedChild(rootElem: IComponent, name: string) {
  return findComponent(rootElem, e => {
    return e?.options?.name === name;
  });
}

export function buildQueryString(params) {
  const qs = _(params)
    .chain()
    .omit(_.isNull as any)
    .map(
      (val, key) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(val))}`,
    )
    .join('&')
    .value();
  return _.isEmpty(qs) ? '' : `?${qs}`;
}

export function buildUrl({ baseUrl, path = '', queryParams = {} }) {
  const url = [baseUrl.replace(/\/$/, ''), path.replace(/^\//, '')]
    .filter(_.negate(_.isEmpty))
    .join('/');
  return `${url}${buildQueryString(queryParams)}`;
}

export function isSynced(model: IModel) {
  return model?.getSynced() === model?.toJSON();
}

export const useInterval = (callback, delay) => {
  const savedCallback = useRef(null);

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      (savedCallback.current as () => void)();
    }
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
};

let requestedUsers = [];

export const clearRequestedUserIds = () => {
  requestedUsers = [];
};

export const addRequestedUser = userMetadata => {
  requestedUsers.push(userMetadata);
};

const buildTeamForecastUsers = (_model, user) => {
  const defaultUser = App.user.toJSON();
  const { id: userId = defaultUser?.id ?? '' } = user;
  const reporteeEndpoint = `${App.api.buildURL(
    `Forecasts/reportees/${userId}`,
  )}`; // the 'level: -1' param is specified for the reportees endpoint, but does not currently work

  App.api.call('read', reporteeEndpoint, -1, {
    success(reporteeData, _request) {
      function provideReportees(_reporteesTree, _rootUserId = '') {
        if (_.isArray(_reporteesTree)) {
          const isRootAManager = _.includes(
            _.map(_reporteesTree, ['metadata', 'id'] as unknown as string),
            _rootUserId,
          );

          if (isRootAManager) {
            const _user = _.find(
              _reporteesTree,
              _reportee => _reportee?.metadata?.id == _rootUserId,
            );
            const children = _.reject(
              _user?.children,
              _reportee => _reportee?.metadata?.id === _rootUserId,
            );
            _.forEach(children.concat(_user), _reportee =>
              provideReportees(_reportee, _reportee?.metadata?.id),
            );
          } else {
            // find user that has the _rootUserId in children
            const parentUser = _.find(_reporteesTree, _reportee => {
              const childrenIds = _.map(_reportee?.children, [
                'metadata',
                'id',
              ] as unknown as string);
              return _.includes(childrenIds, _rootUserId);
            });

            const _user = _.find(
              parentUser?.children,
              _reportee => _reportee?.metadata?.id === _rootUserId,
            );

            provideReportees(_user, _rootUserId);
          }
        } else {
          const _parentUser = _reporteesTree?.metadata;
          const childrenWithParent = _.map(_reporteesTree?.children, [
            'metadata',
          ]);
          const children = _.reject(
            childrenWithParent,
            ({ id }: any) => id === _parentUser?.id,
          );

          // only pull hierarchical userIds
          const isRootUserInChildren =
            _parentUser?.id !== _rootUserId &&
            _.some(children as any[], ({ id }) => id === _rootUserId);

          const prefilteredUsers = isRootUserInChildren
            ? [_parentUser]
            : _.uniq(children.concat(_parentUser));

          const requestedUserIds = _.map(requestedUsers, 'id');

          const shouldRequestUsers = _.reject(prefilteredUsers, ({ id }) =>
            _.includes(requestedUserIds, id),
          );

          _.forEach(shouldRequestUsers, ({ id, full_name }) => {
            const _user = { id, full_name };
            addRequestedUser(_user);
            buildTeamForecastUsers(_model, {
              id,
            });
          });
        }
      }

      const incompleteReporteesList: string[] =
        (getDynamicValues()[DynamicFields.Forecasts.reportees] as string[]) ??
        [];

      // build array of reportees from tree
      const _userId: string = _.last(_request?.params?.url?.split('/'));
      provideReportees(reporteeData, _userId);
      const reportees: string[] = incompleteReporteesList?.concat(
        _.map(requestedUsers, 'full_name'),
      );

      const dynamicValueReporteeList = _.reject(
        _.uniq(reportees),
        _.isUndefined,
      ) as string[];

      setDynamicValues({
        [DynamicFields.Forecasts.reportees]: _.isEmpty(dynamicValueReporteeList)
          ? null
          : dynamicValueReporteeList,
      });
    },
    error(errMsg) {
      console.log(errMsg);
    },
  });
};

export const doForecastsSelectedUserChange = (_model, user) => {
  // clear current dynamic value
  clearRequestedUserIds();

  const forecastType = App.utils.getForecastType(
    user?.is_manager,
    user?.showOpps,
  );
  const isTeamForecast = forecastType.toUpperCase() === 'ROLLUP';

  addRequestedUser(user);

  setDynamicValues({
    [DynamicFields.Forecasts.reportees]: [user?.full_name],
  });

  if (isTeamForecast) {
    buildTeamForecastUsers(_model, user);
  }
};

export const doForecastsTimePeriodChange = _timePeriodModel => {
  /**
   * The timePeriodModel has the incorrect start/end date - it only has the changed label hash
   * The start/end dates will be updated afterwards. Retrieving the model data via setTimeout is not ideal
   */
  setTimeout(() => {
    const timePeriodModel = App?.controller?.context;
    const {
      attributes: {
        selectedTimePeriodStartEnd: { start = null, end = null } = {},
      } = {},
    } = timePeriodModel;

    const formatDateValue = timeValue => {
      const datePattern = /\d\d\d\d-\d\d-\d\d/;
      return datePattern.test(timeValue) ? `TODAY("${timeValue}")` : timeValue;
    };

    setDynamicValues({
      [DynamicFields.Forecasts.timeperiodstart as string]:
        formatDateValue(start),
      [DynamicFields.Forecasts.timeperiodend as string]: formatDateValue(end),
    });
  }, 100);
};

export const getAppMeta = (model: IModel) => {
  return App.utils.deepCopy(
    model?.get('metadata') ?? {
      dashlets: [],
    },
  );
};

const getUserDashboardMeta = (modelId: string) => {
  const componentLastStateKey = 'dashboard-filters';
  const dashboardKey = `Dashboards:${modelId}`;
  const lastStateKey = App.user.lastState.buildKey(
    componentLastStateKey,
    App.user.id,
    dashboardKey,
  );

  return App.utils.deepCopy(App.user.lastState.get(lastStateKey));
};

// Prefix for every analytics failure. These go to console.error, never a bare return: the dashlet
// runs inside the Sugar page, so a warning is the only sign that an event was not sent.
const ANALYTICS_ERROR_PREFIX = 'Sugar Discover analytics:';

// Same order as the private _getInstanceId in Mango's gainsightanalyticsconnector.js, which Sugar
// uses as the Gainsight PX account id. Replicated rather than called because it is not exported.
export const getInstanceId = (): string => {
  const serverInfo = App?.metadata?.getServerInfo?.() ?? {};
  return (
    serverInfo.site_id ||
    serverInfo.si_id ||
    serverInfo.si_name ||
    window.location.hostname ||
    window.location.origin
  );
};

// Dashlet events are explicit custom events sent through the host Sugar page's GainsightPX
// connector. PX auto-tracking cannot see them: the dashlet renders into a shadow root
// (discover-dashlet.elem.tsx), and auto-tracking's DOM traversal does not cross it.
// See docs/modules/ui-service-analytics-events.md.
const fireTrackEvent = (eventData: IEventData) => {
  const eventName = gainsightConstants.reportLoaded;
  const track = App?.analytics?.connectors?.GainsightPX?.track;

  if (!_.isFunction(track)) {
    console.error(
      `${ANALYTICS_ERROR_PREFIX} the host page has no GainsightPX connector; "${eventName}" (source ${eventData.source}) was not sent.`,
    );
    return;
  }

  if (!eventData.tenantId) {
    console.error(
      `${ANALYTICS_ERROR_PREFIX} tenant id is missing; sending "${eventName}" (source ${eventData.source}) without it.`,
    );
  }

  try {
    track(eventName, {
      surface: 'dashlet',
      ...eventData,
      instanceId: getInstanceId(),
    });
  } catch (error) {
    // The connector exists but its tag never loaded, for example when Sugar found no PX
    // environment id, so the global aptrinsic it calls is undefined.
    console.error(
      `${ANALYTICS_ERROR_PREFIX} "${eventName}" (source ${eventData.source}) failed to send.`,
      error,
    );
  }
};

export const trackFocusDrillLoaded = (
  drillVizId: string,
  { view, tenantId, sugarModule }: IFocusDrillData,
) => {
  fireTrackEvent({
    source: 'focus-drill',
    dashboardName: 'Focus Drill',
    dashletLabel: view?.name,
    vizId: drillVizId,
    sugarModule,
    tenantId,
  });
};

export const trackReportLoaded = (
  dashletId: string,
  model: IModel,
  sugarModule: string,
) => {
  const appMeta = getAppMeta(model);
  const thisDashlet = findDashletById(appMeta.dashlets, dashletId);

  if (!thisDashlet) {
    console.error(
      `${ANALYTICS_ERROR_PREFIX} dashlet ${dashletId} is not in the dashboard metadata; "${gainsightConstants.reportLoaded}" was not sent.`,
    );
    return;
  }

  fireTrackEvent({
    source: 'dashlet',
    dashboardName: model.attributes.name,
    dashletLabel: thisDashlet.view.label,
    vizId: thisDashlet.view.vizId,
    sugarModule,
    tenantId: thisDashlet.view.tenantId,
  });
};

export const findDashletById = (dashlets, dashletId) =>
  _.find(dashlets, dashlet => _.isEqual(dashlet?.view?.dashletId, dashletId));

export const setupDashboardFilters = (
  dashletId: string,
  model: IModel,
): IDashboardFilter => {
  const appMeta = getAppMeta(model);
  const userDashboardMeta = getUserDashboardMeta(model.get('id'));
  const thisDashlet = findDashletById(appMeta.dashlets, dashletId);

  if (thisDashlet) {
    const filters = appMeta.filters || {};
    if (!_.isEmpty(userDashboardMeta?.filters)) {
      _.assign(filters, userDashboardMeta?.filters);
    }
    const allFilters = _.filter(filters, v =>
      _.some(v.fields, { dashletId: thisDashlet.id }),
    );
    const vizId = thisDashlet.view?.vizId ?? '';
    const filterDefs: IDashletFilter = allFilters.reduce((acc, item) => {
      _.filter(item.fields, { dashletId: thisDashlet.id }).forEach(field => {
        if (!_.isEmpty(vizId)) {
          if (!acc[vizId]) {
            acc[vizId] = {};
          }
          acc[vizId][`${field.tableKey}:${field.fieldDef.name}`] = {
            fieldDef: field.fieldDef,
            filterDef: item.filterDef,
            targetModule: field.tableKey,
          };
        }
      });
      return acc;
    }, {});
    return { vizId, filterDefs };
  } else {
    return { vizId: undefined, filterDefs: {} };
  }
};
